import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

// Facebook SVG logo
function FacebookIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

// Instagram SVG logo
function InstagramIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
  );
}

export default function PublicFooter() {
  const currentYear = 2026;

  return (
    <footer className="bg-primary text-white/80 mt-auto">
      {/* Top bar */}
      <div className="border-b border-white/10">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-3 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-white/60">
          <div className="flex flex-wrap items-center gap-4">
            <a href="tel:0333962356" className="flex items-center gap-1.5 hover:text-accent transition-colors">
              <Icon name="PhoneIcon" size={13} variant="outline" className="text-accent" />
              033 396 2356
            </a>
            <a href="mailto:golf@maritzburggolfclub.co.za" className="flex items-center gap-1.5 hover:text-accent transition-colors">
              <Icon name="EnvelopeIcon" size={13} variant="outline" className="text-accent" />
              golf@maritzburggolfclub.co.za
            </a>
            <span className="flex items-center gap-1.5">
              <Icon name="MapPinIcon" size={13} variant="outline" className="text-accent" />
              New England Rd, PMB, 3201
            </span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="https://www.facebook.com/maritzburggolfclub/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 hover:text-accent transition-colors"
              aria-label="Facebook"
            >
              <FacebookIcon size={13} />
              Facebook
            </a>
            <span className="text-white/20">|</span>
            <a
              href="https://www.instagram.com/maritzburggolfclub_pmb"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 hover:text-accent transition-colors"
              aria-label="Instagram"
            >
              <InstagramIcon size={13} />
              Instagram
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <AppLogo size={48} />
              <div>
                <div className="text-white font-bold text-base leading-none">Maritzburg</div>
                <div className="text-accent text-xs font-medium tracking-widest uppercase">Golf Club</div>
              </div>
            </div>
            <p className="text-sm leading-relaxed text-white/60 mb-4">
              South Africa&apos;s oldest playing golf club. An 18-hole championship course in the heart of Pietermaritzburg, KwaZulu-Natal. Est. 1886.
            </p>
            <div className="flex gap-3">
              <a
                href="https://www.facebook.com/maritzburggolfclub/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#1877F2] hover:text-white transition-colors duration-150"
                aria-label="Follow Maritzburg Golf Club on Facebook"
              >
                <FacebookIcon size={16} />
              </a>
              <a
                href="https://www.instagram.com/maritzburggolfclub_pmb"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#E1306C] hover:text-white transition-colors duration-150"
                aria-label="Follow Maritzburg Golf Club on Instagram"
              >
                <InstagramIcon size={16} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-4 tracking-wide uppercase">Quick Links</h4>
            <ul className="space-y-2.5">
              {[
                { label: 'Book a Round', href: '/online-booking' },
                { label: 'Events Calendar', href: '/online-booking' },
                { label: 'Loyalty Programme', href: '/online-booking' },
                { label: 'Membership', href: '/#membership' },
                { label: 'FAQ', href: '/faq' },
                { label: 'Contact Us', href: '/contact' },
                { label: 'Admin Login', href: '/administration-dashboard' },
              ]?.map((link) => (
                <li key={`footer-${link?.href}-${link?.label}`}>
                  <Link
                    href={link?.href}
                    className="text-sm text-white/60 hover:text-accent transition-colors duration-150"
                  >
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-4 tracking-wide uppercase">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2.5 text-sm text-white/60">
                <Icon name="PhoneIcon" size={15} variant="outline" className="mt-0.5 shrink-0 text-accent" />
                <a href="tel:0333962356" className="hover:text-accent transition-colors">033 396 2356</a>
              </li>
              <li className="flex items-start gap-2.5 text-sm text-white/60">
                <Icon name="EnvelopeIcon" size={15} variant="outline" className="mt-0.5 shrink-0 text-accent" />
                <a href="mailto:golf@maritzburggolfclub.co.za" className="hover:text-accent transition-colors">golf@maritzburggolfclub.co.za</a>
              </li>
              <li className="flex items-start gap-2.5 text-sm text-white/60">
                <Icon name="MapPinIcon" size={15} variant="outline" className="mt-0.5 shrink-0 text-accent" />
                <span>New England Rd, PMB, 3201<br />Hayfields, KZN</span>
              </li>
            </ul>
          </div>

          {/* Hours */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-4 tracking-wide uppercase">Hours</h4>
            <ul className="space-y-2 text-sm text-white/60">
              <li className="flex justify-between gap-4">
                <span>Mon – Fri</span>
                <span>6:30am – 5:00pm</span>
              </li>
              <li className="flex justify-between gap-4">
                <span>Sat – Sun</span>
                <span>6:00am – 5:00pm</span>
              </li>
            </ul>
            <div className="mt-6">
              <h4 className="text-white font-semibold text-sm mb-3 tracking-wide uppercase">Pro Shop</h4>
              <ul className="space-y-2 text-sm text-white/60">
                <li className="flex justify-between gap-4">
                  <span>Mon – Fri</span>
                  <span>7:00am – 4:30pm</span>
                </li>
                <li className="flex justify-between gap-4">
                  <span>Sat – Sun</span>
                  <span>6:30am – 4:30pm</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/40">
          <span>©{currentYear}. Maritzburg Golf Club. All Rights Reserved.</span>
          <div className="flex gap-4">
            <Link href="/legal#privacy" className="hover:text-white/60 transition-colors">Privacy Policy</Link>
            <Link href="/legal#terms" className="hover:text-white/60 transition-colors">Terms of Use</Link>
            <Link href="/legal#cookies" className="hover:text-white/60 transition-colors">Cookie Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}